import { useMemo } from 'react';
import type React from 'react';

import { useLocation } from 'react-router';

import { parseHtml } from '@change-corgi/core-react/html';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { RESTRICTED_ATTRS, RESTRICTED_TAGS } from './constants';
import { genReplaceTags } from './genReplaceTags';

/**
 * Renders sanitized/parsed UGC HTML (e.g. `PetitionSupporterComment.text`) as
 * a `React.ReactNode`, for design-system components whose content prop expects that rather than a
 * raw HTML string - e.g. `CommentCard.commentContent`, which does no sanitizing/parsing of its own:
 * `<CommentCard commentContent={<HtmlComment html={comment.text} />} ... />`. `expressive: true`
 * matches `CommentCard`'s italic quote styling - `Link`/`Text` default `expressive` to `false`, so
 * an autolinked `Link` without it renders in a mismatched font.
 */
export const HtmlComment: React.FC<Props> = ({ html }) => {
	const location = useLocation();
	const utilityContext = useUtilityContext();

	const replace = useMemo(
		() => genReplaceTags({ expressive: true }, location, utilityContext),
		[location, utilityContext],
	);

	return <>{parseHtml(html, { replace, restrictedTags: RESTRICTED_TAGS, restrictedAttrs: RESTRICTED_ATTRS })}</>;
};

type Props = {
	html: string;
};
